/**
 * Convert epoch timestamp to a readable date string
 * @param {number} epoch - Unix timestamp in seconds
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatEpochDate = (epoch, options = {}) => {
  if (!epoch || typeof epoch !== 'number') {
    return 'N/A';
  }
  
  // Convert seconds to milliseconds if needed
  const timestamp = epoch < 10000000000 ? epoch * 1000 : epoch;
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return new Date(timestamp).toLocaleDateString('en-US', defaultOptions);
};

/**
 * Convert epoch timestamp to a readable date and time string
 * @param {number} epoch - Unix timestamp in seconds
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date and time string
 */
export const formatEpochDateTime = (epoch, options = {}) => {
  if (!epoch || typeof epoch !== 'number') {
    return 'N/A';
  }
  
  // Convert seconds to milliseconds if needed
  const timestamp = epoch < 10000000000 ? epoch * 1000 : epoch;
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return new Date(timestamp).toLocaleString('en-US', defaultOptions);
};

/**
 * Get time ago string from epoch timestamp
 * @param {number} epoch - Unix timestamp in seconds
 * @returns {string} Time ago string (e.g., "2 days ago", "3 hours ago")
 */
export const getTimeAgo = (epoch) => {
  if (!epoch || typeof epoch !== 'number') {
    return 'N/A';
  }
  
  // Convert seconds to milliseconds if needed
  const timestamp = epoch < 10000000000 ? epoch * 1000 : epoch;
  
  const now = new Date();
  const past = new Date(timestamp);
  const diffInSeconds = Math.floor((now - past) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};
